import clsx from 'clsx'
import { useFormik } from 'formik'
import { useState } from 'react'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { ServerError, getError } from '../../../../_metronic/helpers'
import { requestPasswordReset } from '../core/_requests'

type PasswordResetDto = {
  email: string
}

const initialValues: PasswordResetDto = {
  email: '',
}

const requestPasswordResetSchema: Yup.SchemaOf<PasswordResetDto> = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("An email address is required")
})

export function RequestPasswordReset() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const requestPasswordResetMutation = useMutation(
    requestPasswordReset,
  )

  const formik = useFormik({
    initialValues,
    validationSchema: requestPasswordResetSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setStatus(null)
      setLoading(true)
      setSuccess(false)

      requestPasswordResetMutation.mutate(values, {
        onSettled: () => {
          setLoading(false)
          setSubmitting(false)
        },
        onSuccess: () => {
          setSuccess(true)
          setStatus(`Password reset email sent to: ${values.email}`)
        },
        onError: (errors: ServerError) => {
          setSuccess(false)
          setStatus(getError(errors))
        }
      })
    },
  })

  return (
    <>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-10'>
          {/* begin::Title */}
          <h1 className='text-dark mb-3'>Forgot Password?</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-gray-400 fw-bold fs-4'>Enter your email to reset your password.</div>
          {/* end::Link */}
        </div>

        {/* begin::Title */}
        {formik.status && (
          success ? (
            <div className="mb-10 bg-light-info p-8 rounded">
              <div className='text-info'>{formik.status}</div>
            </div>
          ) : (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )
        )}
        {/* end::Title */}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>
          <input
            type='email'
            placeholder='Email'
            autoFocus={true}
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': formik.touched.email && formik.errors.email },
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <button
            type='submit'
            id='kt_password_reset_submit'
            className='btn btn-lg btn-primary fw-bolder me-4'
          >
            <span className='indicator-label'>Submit</span>
            {loading && (
              <span className='indicator-progress'>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/login'>
            <button
              type='button'
              id='kt_login_password_reset_form_cancel_button'
              className='btn btn-lg btn-light-primary fw-bolder'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              Cancel
            </button>
          </Link>{' '}
        </div>
        {/* end::Form group */}
      </form>
    </>
  )
}
